/**
 * ACTIVE DELIVERY SCREEN — Full Delivery Lifecycle
 *
 * Complete driver workflow from acceptance to completion:
 *   Accepted → Scheduled → Start → En Route to Seller → Arrived Pickup →
 *   Pickup Checklist → Seller Signature → En Route to Buyer →
 *   Arrived Delivery → Delivery Checklist → Buyer Signature → Complete
 *
 * Context-aware communication:
 *   Before pickup: seller-focused (call/message seller, navigate to pickup)
 *   After pickup: buyer-focused (call/message buyer, navigate to delivery)
 *
 * Calls/messages routed through OpenPhone proxy — no real numbers exposed.
 * GPS tracking every 30 seconds. Build: 20260410b
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Platform, Image, Dimensions,
  ActivityIndicator, ScrollView, Linking, Alert, Modal, Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch } from 'react-redux';
import { colors, spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { JobStatus, JOB_WORKFLOW_STEPS, workflowFor } from '../../types/job';
import { Alert as RNAlert } from 'react-native';
import { getTrainingCategoryLabel } from '../../utils/categoryMapping';
import { advanceJobStatus, addActiveJob, completeJob, addEarnings, removeActiveJob, RootState } from '../../store';
import { useSelector } from 'react-redux';
import { RouteMap } from '../../components/RouteMap';
import * as Location from 'expo-location';
import { startBackgroundTracking, stopBackgroundTracking } from '../../services/backgroundLocation';
import { ensureLocationEnabled } from '../../services/location';
import { connectSocket } from '../../services/socket';

import { LightbulbBody } from '../../components/LightbulbBody';
import { prefetchJobBrief, markJobBriefViewed, briefWasViewed } from '../../services/jobBrief';
import { pairPickupWithDelivery } from '../../utils/pickupWindow';
import { driverWindowStart, driverWindowEnd, bufferedPickup, fmtTwoDayDelivery } from '../../utils/windows';
import { proxyMessaging } from '../../services/proxy-messaging';
import { PREDISPATCH_ENABLED } from '../../config/flags';
import { CoiCard } from '../../components/CoiCard';

const STEP_ICONS: Record<string, string> = {
  ACCEPTED: 'checkmark-circle',
  SCHEDULED: 'calendar',
  STARTED: 'play-circle',
  EN_ROUTE_TO_SELLER: 'navigate',
  ARRIVED_PICKUP: 'location',
  PICKUP_CHECKLIST_COMPLETE: 'clipboard',
  SELLER_SIGNATURE_COMPLETE: 'create',
  LOADED_PHOTO_COMPLETE: 'camera',
  EN_ROUTE_TO_BUYER: 'navigate',
  ARRIVED_DELIVERY: 'location',
  DELIVERY_CHECKLIST_COMPLETE: 'clipboard',
};

/** Human-friendly instruction for what the driver should do RIGHT NOW */
const STEP_DRIVER_INSTRUCTION: Record<string, string> = {
  ACCEPTED: 'Schedule Pickup',
  SCHEDULED: 'Head to Pickup',
  STARTED: 'Head to Pickup',
  EN_ROUTE_TO_SELLER: 'Driving to Pickup',
  ARRIVED_PICKUP: 'Complete Pickup Checklist',
  PICKUP_CHECKLIST_COMPLETE: 'Get Seller Signature',
  SELLER_SIGNATURE_COMPLETE: 'Take Loaded Photo',
  LOADED_PHOTO_COMPLETE: 'Head to Delivery',
  EN_ROUTE_TO_BUYER: 'Driving to Delivery',
  ARRIVED_DELIVERY: 'Complete Delivery Checklist',
  DELIVERY_CHECKLIST_COMPLETE: 'Get Buyer Signature',
  AWAITING_DELIVERY_PAYMENT: 'Complete Delivery',
  BUYER_SIGNATURE_COMPLETE: 'Complete Delivery',
};

const PICKUP_STATUSES = ['ACCEPTED', 'SCHEDULED', 'STARTED', 'EN_ROUTE_TO_SELLER', 'ARRIVED_PICKUP', 'PICKUP_CHECKLIST_COMPLETE', 'SELLER_SIGNATURE_COMPLETE', 'LOADED_PHOTO_COMPLETE'];

function isPickupPhase(status: JobStatus): boolean {
  return PICKUP_STATUSES.includes(status);
}

export function ActiveJobScreen({ route, navigation }: any) {
  const dispatch = useDispatch();
  const { jobId, job: passedJob } = route.params;
  const [job, setJob]             = useState<any>(passedJob || null);
  const [loading, setLoading]     = useState(!passedJob);
  const alreadyCompleted = useSelector((s: RootState) => s.jobs.completedJobs.some(j => j.id === jobId));
  const driverProfile = useSelector((s: RootState) => s.auth.driver);
  const isFleetMember = !!(driverProfile as any)?.fleetManagerId && !(driverProfile as any)?.isFleetManager;
  const [advancing, setAdvancing] = useState(false);
  const [driverLocation, setDriverLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const trackInterval = useRef<any>(null);
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);
  const [selectingWindow, setSelectingWindow] = useState(false);
  const [deliveryWindows, setDeliveryWindows] = useState<any[]>([]);
  const [showTips, setShowTips] = useState(false);
  const [showDetailedTips, setShowDetailedTips] = useState(false);
  const [tipsViewed, setTipsViewed] = useState(false);
  // Set when Start was tapped before the lightbulb was opened: the sheet opens with this line.
  const [tipsGateNote, setTipsGateNote] = useState<string | null>(null);
  const tipsPulse = useRef(new Animated.Value(1)).current;

  // ── PERMANENT pre-dispatch gate ────────────────────────────────────────────
  // Whenever the driver opens this active job (and on every screen focus) while it's
  // still pre-pickup with an un-submitted pre-dispatch checklist, force the checklist
  // as a full-screen popup. They cannot use the active-delivery screen — or Start —
  // until it's submitted. Re-checks on focus so backing out just bounces them back.
  const predispatchGateRef = useRef(false);
  useEffect(() => {
    if (!PREDISPATCH_ENABLED) return;
    const PRE_PICKUP = ['ACCEPTED', 'ASSIGNED', 'SCHEDULED', 'CONFIRMED'];
    const check = async () => {
      const j = job;
      if (!j || !PRE_PICKUP.includes(j.status)) return;
      // never gate a job reassigned to a different fleet driver
      if (j.assignedFleetDriverId && j.assignedFleetDriverId !== (driverProfile as any)?.id) return;
      if (predispatchGateRef.current) return;
      try {
        const { data } = await api.get(`/jobs/${jobId}/predispatch`);
        const pending = !!data?.status && data.status !== 'PASSED' && !data?.completedAt;
        // `due` (server-side: scheduled, today or tomorrow in the job's timezone) is what
        // stops this bouncing a driver back into the checklist for a job days away every
        // time the screen regains focus. The bounce itself is deliberate and stays — it is
        // the head-out gate — but it now only applies once the job is actually imminent.
        // A driver who heads out early is still caught reactively: the backend answers
        // PREDISPATCH_REQUIRED and routeToChecklist() below takes them there regardless.
        if (pending && data?.due === true) {
          predispatchGateRef.current = true;
          navigation.navigate('PreDispatchChecklist', { jobId, job: j });
        }
      } catch { /* fail open on network/404 */ }
    };
    check();
    const unsub = navigation.addListener('focus', () => { predispatchGateRef.current = false; check(); });
    return unsub;
  }, [job?.status, jobId, navigation, (driverProfile as any)?.id]);

  // Track if tips have been viewed for this job (persisted)
  useEffect(() => {
    if (!jobId) return;
    AsyncStorage.getItem(`tips_viewed_${jobId}`).then(val => {
      if (val === 'true') setTipsViewed(true);
    }).catch(() => {});
  }, [jobId]);

  // Opened on another phone, or before a reinstall: the server remembers (Job.briefViewedAt).
  useEffect(() => { if ((job as any)?.briefViewedAt) setTipsViewed(true); }, [(job as any)?.briefViewedAt]);
  // Warm the lightbulb's brief in the background; never awaited, the screen does not wait.
  useEffect(() => { prefetchJobBrief(jobId); }, [jobId]);
  const openTips = useCallback((gateNote?: string) => {
    setTipsGateNote(gateNote || null);
    setShowTips(true);
    setTipsViewed(true);
    markJobBriefViewed(jobId);
  }, [jobId]);

  // Flashing animation for tips FAB
  useEffect(() => {
    if (tipsViewed) return;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(tipsPulse, { toValue: 0.2, duration: 600, useNativeDriver: true }),
        Animated.timing(tipsPulse, { toValue: 1, duration: 600, useNativeDriver: true }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [tipsViewed, tipsPulse]);

  // Fleet assignment — try ALL methods to detect fleet manager status
  const [fleetMembers, setFleetMembers] = useState<any[]>([]);
  const [showFleetAssign, setShowFleetAssign] = useState(false);
  const [assigningFleet, setAssigningFleet] = useState(false);
  const [confirmedFleetManager, setConfirmedFleetManager] = useState<boolean | null>(null);

  // Triple-source fleet detection: job API, profile API, and Redux cache
  useEffect(() => {
    // Source 1: job._isFleetManager from backend
    if (job?._isFleetManager) {
      setConfirmedFleetManager(true);
      if (job._fleetMembers) setFleetMembers(job._fleetMembers);
      return;
    }
    // Source 2: direct API call
    api.get('/drivers/me/fleet-members')
      .then(r => {
        if (Array.isArray(r.data)) {
          setConfirmedFleetManager(true);
          setFleetMembers(r.data);
        }
      })
      .catch(() => {
        // Source 3: Redux profile as last resort
        if ((driverProfile as any)?.isFleetManager) {
          setConfirmedFleetManager(true);
        } else {
          setConfirmedFleetManager(false);
        }
      });
  }, [job?._isFleetManager, job?._fleetMembers]);

  const isFleetManager = confirmedFleetManager === true;
  const isFleetViewOnly = isFleetManager && !!job?.assignedFleetDriverId && job.assignedFleetDriverId !== driverProfile?.id;
  const needsFleetAssignment = isFleetManager && !job?.assignedFleetDriverId;

  const handleFleetAssign = async (fleetDriverId: string) => {
    setAssigningFleet(true);
    try {
      await api.post(`/jobs/${jobId}/assign-fleet`, { fleetDriverId });
      const assignedId = fleetDriverId === 'self' ? driverProfile?.id : fleetDriverId;
      const assignedName = fleetDriverId === 'self'
        ? (driverProfile as any)?.name || 'You'
        : fleetMembers.find(m => m.id === fleetDriverId)?.name || 'Driver';
      setJob((prev: any) => ({ ...prev, assignedFleetDriverId: assignedId, assignedFleetDriverName: assignedName }));
      setShowFleetAssign(false);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Could not assign driver');
    }
    setAssigningFleet(false);
  };

  // Fleet manager viewing the assigned driver's job: keep a live ref (so this
  // device doesn't send ITS GPS for a job it isn't driving) and POLL the job every
  // 15s so the manager sees the assigned driver's CURRENT STEP update in real time.
  const fleetViewOnlyRef = useRef(false);
  useEffect(() => { fleetViewOnlyRef.current = isFleetViewOnly; }, [isFleetViewOnly]);
  // Guards a one-time bounce-home when a job is unassigned out from under the driver.
  const reconciledAwayRef = useRef(false);
  // Guards a double-tap on "Complete Delivery" (addEarnings is not idempotent).
  const finalizingRef = useRef(false);
  useEffect(() => {
    if (!isFleetViewOnly) return;
    const id = setInterval(() => { loadJob(); }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFleetViewOnly, jobId]);

  // Dedicated effect: always fetch delivery windows from API
  useEffect(() => {
    api.get(`/jobs/${jobId}`).then(({ data }) => {
      if (data?.deliveryWindows?.length > 0) {
        setDeliveryWindows(data.deliveryWindows);
      }
    }).catch(() => {});
  }, [jobId]);

  // Re-reconcile on focus: if the job was reassigned/unassigned/cancelled while the driver
  // was on another screen, loadJob() drops it (and bounces home if unassigned) on return.
  useEffect(() => {
    const unsub = navigation.addListener('focus', () => { loadJob(); });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, jobId]);

  useEffect(() => {
    // Ensure this job is tracked in Redux active jobs
    if (passedJob) {
      dispatch(addActiveJob(passedJob));
    }
    // Always fetch full job data from API (passedJob may be missing deliveryWindows, notes, etc.)
    loadJob();

    // ── Listen for admin-initiated status changes via socket ──
    const socket = connectSocket();
    const handleStatusChange = (data: { jobId: string; status: string }) => {
      if (data.jobId === jobId && data.status) {
        setJob((prev: any) => prev ? { ...prev, status: data.status as JobStatus } : prev);
        // advanceJobStatus only UPDATES a job already in the active list. Using addActiveJob
        // here would PUSH a stub {id,status} with no title/addresses if the job wasn't present,
        // rendering a blank card on Home.
        dispatch(advanceJobStatus({ jobId, status: data.status as JobStatus }));
      }
    };
    socket.on('job:status', handleStatusChange);

    // ── GPS Tracking — Multi-layer approach for maximum reliability ──
    // Layer 1: watchPosition (primary for web — most persistent browser API)
    // Layer 2: setInterval fallback (catches cases where watchPosition stalls)
    // Layer 3: visibilitychange handler (immediate update when app returns to foreground)
    // Layer 4: Wake Lock (prevents screen sleep)
    // Layer 5: Native background tracking via expo-task-manager

    let watchId: number | null = null;
    let lastSentAt = 0;
    let lastWatchFire = Date.now();
    let wakeLock: any = null;
    // Last coordinate we pushed into the store (which re-renders the map). watchPosition with
    // enableHighAccuracy fires many times/sec; calling setDriverLocation on EVERY raw fix
    // re-rendered the active-job screen + live map continuously and froze the phone while driving.
    let lastStoreLat = NaN, lastStoreLng = NaN;
    const distMeters = (aLat: number, aLng: number, bLat: number, bLng: number) => {
      if (isNaN(aLat) || isNaN(aLng)) return Infinity;
      const R = 6371000, toR = (d: number) => (d * Math.PI) / 180;
      const dLat = toR(bLat - aLat), dLng = toR(bLng - aLng);
      const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(aLat)) * Math.cos(toR(bLat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(s));
    };

    const sendLocation = (latitude: number, longitude: number, heading?: number | null, speed?: number | null, force = false) => {
      // A fleet manager only VIEWING the assigned driver's job must NOT post their
      // own GPS as the job's location (it would hijack the live tracking pin).
      if (fleetViewOnlyRef.current) return;
      // Only re-render the map when we've actually moved (>=8m) — not on GPS jitter every tick.
      if (force || distMeters(lastStoreLat, lastStoreLng, latitude, longitude) >= 8) {
        lastStoreLat = latitude; lastStoreLng = longitude;
        setDriverLocation({ latitude, longitude });
      }
      const now = Date.now();
      if (force || now - lastSentAt >= 15000) {
        lastSentAt = now;
        api.post('/tracking/update', { jobId, latitude, longitude, heading, speed }).catch(() => {});
      }
    };

    const acquireWakeLock = async () => {
      if (Platform.OS === 'web' && 'wakeLock' in navigator) {
        try {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          // Re-acquire on release (happens when tab loses focus briefly)
          wakeLock.addEventListener('release', () => {
            wakeLock = null;
            // Try to re-acquire after a short delay
            setTimeout(acquireWakeLock, 1000);
          });
        } catch {}
      }
    };

    // Initial position + native background tracking
    (async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status === 'granted') {
          const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
          sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed, true);
        }
        // A fleet manager only VIEWING the assigned member's job must NOT start background
        // tracking — it bypasses the sendLocation fleet-view guard and would post the
        // MANAGER's GPS as the job's live location (hijacking the pin). Only the driver
        // actually performing the job tracks in the background.
        if (Platform.OS !== 'web' && !fleetViewOnlyRef.current) {
          startBackgroundTracking(jobId).catch(() => {});
        }
      } catch {}
    })();

    // Layer 1: watchPosition — fires on every significant position change
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          lastWatchFire = Date.now();
          sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
      );
    }

    // Layer 2: setInterval fallback — catches stalled watchPosition + acts as heartbeat
    // If watchPosition hasn't fired in 45 seconds, force a fresh position fetch
    const intervalId = setInterval(async () => {
      try {
        const watchStale = Date.now() - lastWatchFire > 45000;
        if (Platform.OS !== 'web' || watchStale) {
          const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
          sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed, watchStale);
        }
      } catch {}
    }, 20000);

    // Layer 3: visibilitychange — immediate update when driver returns to app
    const handleVisibility = () => {
      if (Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'visible') {
        // App just came back to foreground — send position immediately
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed, true),
            () => {},
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
          );
        }
        // Re-acquire wake lock (lost when tab was hidden)
        acquireWakeLock();
        // Restart watchPosition if it went stale while backgrounded
        if (watchId !== null && Date.now() - lastWatchFire > 60000) {
          navigator.geolocation.clearWatch(watchId);
          watchId = navigator.geolocation.watchPosition(
            (pos) => {
              lastWatchFire = Date.now();
              sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed);
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
          );
        }
      }
    };
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    // Layer 4: Wake Lock — prevent screen from sleeping during delivery
    acquireWakeLock();

    // Layer 5: Silent audio keep-alive — prevents browser from suspending the PWA
    // when driver switches to Google Maps. Browsers won't freeze tabs playing audio.
    let silentAudio: HTMLAudioElement | null = null;
    let audioResume: (() => void) | null = null;
    if (Platform.OS === 'web') {
      try {
        // Create a tiny silent WAV (44 bytes header + 1 second of silence at 8kHz mono)
        const silentWav = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        silentAudio = new Audio(silentWav);
        silentAudio.loop = true;
        silentAudio.volume = 0.01; // Near-silent
        silentAudio.play().catch(() => {}); // May need user gesture first
        // Re-start on visibility change (some browsers pause audio in background then resume)
        audioResume = () => {
          if (silentAudio && silentAudio.paused) silentAudio.play().catch(() => {});
        };
        document.addEventListener('visibilitychange', audioResume);
      } catch {}
    }

    return () => {
      socket.off('job:status', handleStatusChange);
      clearInterval(intervalId);
      // Stop native background tracking when leaving this job screen (navigate away,
      // cancel, or reassign all unmount it). Previously it only stopped on "Complete
      // Delivery", so a cancelled/abandoned job kept posting ghost breadcrumbs forever.
      // The app-wide RootNavigator heartbeat keeps idle/driver-level location flowing.
      if (Platform.OS !== 'web') {
        stopBackgroundTracking().catch(() => {});
      }
      if (watchId !== null && Platform.OS === 'web' && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
        // Was leaked before: every job switch added another audioResume listener bound to an
        // orphaned audio element, accumulating over a shift and spawning overlapping playback.
        if (audioResume) document.removeEventListener('visibilitychange', audioResume);
      }
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
      if (silentAudio) {
        silentAudio.pause();
        silentAudio.src = '';
        silentAudio = null;
      }
    };
  }, [jobId]);

  // Sync local status to backend on every status change — keeps admin in sync
  // Only sync if status moved FORWARD (prevents race condition from reverting status)
  // Skip if advance/completedAction already sent the transition (prevents duplicate SMS)
  // IMPORTANT: Only sync when job.status changes, NOT when jobId changes (prevents cross-job contamination)
  const lastSyncedStatus = React.useRef<string | null>(null);
  const advancedViaAction = React.useRef<string | null>(null);
  const currentJobIdRef = React.useRef<string>(jobId);
  useEffect(() => {
    // If jobId changed (driver switched to a different job), reset sync state — do NOT sync old status
    if (currentJobIdRef.current !== jobId) {
      currentJobIdRef.current = jobId;
      lastSyncedStatus.current = null;
      advancedViaAction.current = null;
      return; // Don't sync — let loadJob fetch the real status first
    }
    if (job?.status && job?.id === jobId) {
      // Skip if this status was already sent via advance endpoint (completedAction or handleAction)
      if (advancedViaAction.current === job.status) {
        lastSyncedStatus.current = job.status;
        advancedViaAction.current = null;
        return;
      }
      const STATUS_ORDER = [
        'AVAILABLE', 'ACCEPTED', 'SCHEDULED', 'STARTED',
        'EN_ROUTE_TO_SELLER', 'ARRIVED_PICKUP', 'PICKUP_CHECKLIST_COMPLETE',
        'SELLER_SIGNATURE_COMPLETE', 'LOADED_PHOTO_COMPLETE', 'EN_ROUTE_TO_BUYER',
        'ARRIVED_DELIVERY', 'DELIVERY_CHECKLIST_COMPLETE',
        'AWAITING_DELIVERY_PAYMENT', 'BUYER_SIGNATURE_COMPLETE', 'COMPLETED',
      ];
      const currentIdx = STATUS_ORDER.indexOf(job.status);
      const lastIdx = STATUS_ORDER.indexOf(lastSyncedStatus.current || '');
      if (currentIdx > lastIdx) {
        lastSyncedStatus.current = job.status;
        api.post(`/jobs/${jobId}/sync-status`, { status: job.status }).catch(() => {});
      }
    }
  }, [job?.status, jobId]);

  const loadJob = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      // Always update delivery windows from API response
      if (data?.deliveryWindows?.length > 0) {
        setDeliveryWindows(data.deliveryWindows);
      }
      setJob((prev: any) => {
        if (!prev) return data;
        // Don't let backend response overwrite a more advanced local status (race condition fix)
        const STATUS_ORDER = [
          'AVAILABLE', 'ACCEPTED', 'SCHEDULED', 'STARTED',
          'EN_ROUTE_TO_SELLER', 'ARRIVED_PICKUP', 'PICKUP_CHECKLIST_COMPLETE',
          'SELLER_SIGNATURE_COMPLETE', 'LOADED_PHOTO_COMPLETE', 'EN_ROUTE_TO_BUYER',
          'ARRIVED_DELIVERY', 'DELIVERY_CHECKLIST_COMPLETE',
          'AWAITING_DELIVERY_PAYMENT', 'BUYER_SIGNATURE_COMPLETE', 'COMPLETED',
        ];
        const localIdx = STATUS_ORDER.indexOf(prev.status);
        const remoteIdx = STATUS_ORDER.indexOf(data.status);
        const merged = { ...prev, ...data };
        // Honor terminal/reset states from the server even though their index is low/absent:
        // a CANCELLED or unassigned-back-to-board (AVAILABLE) job must NOT be kept "advanced"
        // by the forward-only guard, or the driver keeps driving a job that isn't theirs.
        const serverWins = data.status === 'CANCELLED' || data.status === 'AVAILABLE';
        if (localIdx > remoteIdx && !serverWins) {
          merged.status = prev.status; // Keep the more advanced local status
        }
        return merged;
      });
      // Reconcile ownership: if the server says this job is no longer an active delivery
      // (cancelled, or unassigned back to the board), drop it from the active list. For an
      // unassigned (AVAILABLE) job, leave the screen — the live workflow must not show for a
      // job nobody is driving. A fleet manager viewing a member's job is unaffected (that job
      // stays SCHEDULED+, never AVAILABLE).
      if (data?.status === 'CANCELLED' || data?.status === 'AVAILABLE') {
        dispatch(removeActiveJob(jobId));
        if (data.status === 'AVAILABLE' && !reconciledAwayRef.current) {
          reconciledAwayRef.current = true;
          navigation.goBack();
        }
      }
    } catch {
      if (!job) setJob(null);
    }
    setLoading(false);
  };

  const openNavigation = useCallback((address: string) => {
    const encoded = encodeURIComponent(address);
    const url = Platform.select({
      ios: `maps:?daddr=${encoded}`,
      android: `google.navigation:q=${encoded}`,
      default: `https://www.google.com/maps/dir/?api=1&destination=${encoded}`,
    })!;
    Linking.openURL(url).catch(() => {
      Linking.openURL(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`);
    });
  }, []);

  const handleMessage = useCallback((recipientType: 'BUYER' | 'SELLER') => {
    navigation.navigate('Messaging', { jobId, job, initialRecipient: recipientType });
  }, [jobId, job, navigation]);

  // Proxy call — driver dials a Commonplace number; real numbers stay private.
  const handleCall = useCallback(async (recipientType: 'BUYER' | 'SELLER') => {
    try {
      const { proxyNumber } = await proxyMessaging.initiateCall(jobId, recipientType);
      if (proxyNumber) Linking.openURL(`tel:${proxyNumber.replace(/[^\d+]/g, '')}`).catch(() => {});
    } catch {}
  }, [jobId]);

  const handleAction = useCallback(async (endpoint: string, nextStatus: JobStatus) => {
    if (advancing) return; // prevent double-tap
    // OPEN THE LIGHTBULB BEFORE STARTING (owner, 20 Oct). The first head-out tap (Start, or
    // the first en-route step on a single-leg / delivery-only job) opens the hints instead
    // when they were never opened for this job; once opened (here, on another phone, or
    // recorded server-side) Start works normally. Enforced only in the app: the server
    // logs a start without it but never refuses one, so an older app version still works.
    // A fleet manager only viewing a member's job is not the one driving and is never held.
    const isHeadOut = ['STARTED', 'EN_ROUTE_TO_SELLER', 'EN_ROUTE_TO_BUYER'].includes(nextStatus)
      && ['ACCEPTED', 'SCHEDULED', 'STARTED', 'CONFIRMED', 'ASSIGNED'].includes(String(job?.status));
    if (isHeadOut && !fleetViewOnlyRef.current && !tipsViewed && !(await briefWasViewed(jobId, job as any))) {
      openTips('Open the hints first, then start');
      return;
    }
    // Disable the control immediately so the up-to-15s location check below can't be
    // double-tapped into two concurrent advance flows.
    setAdvancing(true);
    // HARD GATE: starting/heading out requires location. Block the first head-out
    // transition (STARTED / EN_ROUTE_TO_SELLER) unless location is enabled — that's how
    // the customer + admin get live tracking the whole way. A fleet manager merely
    // VIEWING the member's job is exempt (they're not the one driving).
    if (!fleetViewOnlyRef.current && (nextStatus === 'STARTED' || nextStatus === 'EN_ROUTE_TO_SELLER')) {
      const locOk = await ensureLocationEnabled();
      if (!locOk) {
        setAdvancing(false);
        Alert.alert('Location required', 'Please enable location to start this job. Commonplace needs your location for live delivery tracking.');
        return;
      }
    }
    // Force a fresh GPS update on every status transition (fixes stale location on tracking page).
    // A fleet manager only VIEWING the assigned driver's job must NOT post their own GPS here —
    // it would hijack the job's live pin (same guard as the sendLocation loop).
    try {
      if (!fleetViewOnlyRef.current && Platform.OS === 'web' && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            api.post('/tracking/update', {
              jobId, latitude: pos.coords.latitude, longitude: pos.coords.longitude,
              heading: pos.coords.heading, speed: pos.coords.speed,
            }).catch(() => {});
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 },
        );
      } else {
        const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
        api.post('/tracking/update', {
          jobId, latitude: pos.coords.latitude, longitude: pos.coords.longitude,
          heading: pos.coords.heading, speed: pos.coords.speed,
        }).catch(() => {});
      }
    } catch {}
    advancedViaAction.current = nextStatus; // prevent sync-status from duplicating
    // (flag-gated) The backend rejects ANY head-out transition (start /
    // en-route-seller / sync-status) with 400 + code PREDISPATCH_REQUIRED until
    // the pre-dispatch checklist is PASSED. Detect it on any endpoint and route
    // the driver into the checklist — never optimistically advance or force-sync
    // past it.
    const isGate = (payload: any) => PREDISPATCH_ENABLED
      && (payload?.code === 'PREDISPATCH_REQUIRED' || /pre-?dispatch|checklist/i.test(String(payload?.error || '')));
    const routeToChecklist = () => {
      advancedViaAction.current = null;
      setAdvancing(false);
      navigation.navigate('PreDispatchChecklist', { jobId, job });
    };
    // The backend also gates head-out on LOCATION_REQUIRED (start / en-route-seller /
    // sync-status). Surface a clear "enable location" message instead of the generic failure,
    // since the sync-status fallback would be blocked the same way.
    const isLocGate = (payload: any) => payload?.code === 'LOCATION_REQUIRED' || /LOCATION_REQUIRED/.test(String(payload?.error || ''));
    const handleLocGate = () => {
      advancedViaAction.current = null;
      setAdvancing(false);
      Alert.alert('Location required', 'We couldn’t get your location. Make sure location is enabled for Commonplace and try again — the customer needs to track the delivery.');
    };
    try {
      const resp = await api.post(`/jobs/${jobId}/${endpoint}`);
      if (resp.status >= 400) {
        if (isGate(resp.data)) { routeToChecklist(); return; }
        if (isLocGate(resp.data)) { handleLocGate(); return; }
        throw new Error(resp.data?.error || 'Transition failed');
      }
      const updated = { ...job, status: nextStatus };
      setJob(updated);
      dispatch(addActiveJob(updated));
    } catch (err: any) {
      if (isGate(err?.response?.data)) { routeToChecklist(); return; }
      if (isLocGate(err?.response?.data)) { handleLocGate(); return; }
      // Try force-sync as fallback — but it is gated too; do not advance if it rejects.
      try {
        const sresp = await api.post(`/jobs/${jobId}/sync-status`, { status: nextStatus });
        if (sresp?.status >= 400) {
          if (isGate(sresp.data)) { routeToChecklist(); return; }
          if (isLocGate(sresp.data)) { handleLocGate(); return; }
          throw new Error(sresp.data?.error || 'sync failed');
        }
        const updated = { ...job, status: nextStatus };
        setJob(updated);
        dispatch(addActiveJob(updated));
      } catch (e2: any) {
        if (isGate(e2?.response?.data)) { routeToChecklist(); return; }
        if (isLocGate(e2?.response?.data)) { handleLocGate(); return; }
        // Both failed — reset advancedViaAction so sync-status can retry
        advancedViaAction.current = null;
        Alert.alert('Update Failed', 'Could not update status. Please check your connection and try again.');
      }
    }
    setAdvancing(false);
  }, [jobId, job, dispatch, advancing, navigation, tipsViewed, openTips]);

  const handleChecklistNav = useCallback((type: 'pickup' | 'delivery') => {
    navigation.navigate('Checklist', { jobId, type, job });
  }, [jobId, job, navigation]);

  const handleSignatureNav = useCallback((phase: 'seller' | 'buyer') => {
    const type = phase === 'seller' ? 'PICKUP' : 'DELIVERY';
    navigation.navigate('Signature', { jobId, type });
  }, [jobId, navigation]);

  const handleLoadedPhotoNav = useCallback(() => {
    navigation.navigate('LoadedPhoto', { jobId });
  }, [jobId, navigation]);

  // Storage pickups close on the storage drop-off (photos in the unit + the driver's own
  // signature). That screen confirms the STORAGE record, so it needs the StorageJob id the
  // job payload now carries, not the Job id.
  const handleStorageDropOffNav = useCallback(() => {
    const storageJobId = (job as any)?.storageJobId;
    if (!storageJobId) {
      RNAlert.alert('Storage record missing', 'This storage pickup has no storage record yet. Message support before you drop it off.');
      return;
    }
    navigation.navigate('StorageDropOff', { id: storageJobId, productTitle: (job as any)?.productTitle });
  }, [job, navigation]);

  const handleSelectWindow = useCallback(async () => {
    if (!selectedWindowId || selectingWindow) return;
    setSelectingWindow(true);
    advancedViaAction.current = 'SCHEDULED'; // prevent sync-status from duplicating
    try {
      await api.post(`/jobs/${jobId}/select-window`, { windowId: selectedWindowId });
      // Functional update from the LATEST job — a full replace from the render-time `job`
      // snapshot can revert a status the socket/poll advanced between render and this call.
      setJob((prev: any) => {
        const next = prev ? { ...prev, status: 'SCHEDULED' } : prev;
        if (next) dispatch(addActiveJob(next));
        return next;
      });
      await loadJob();
    } catch {
      try {
        await api.post(`/jobs/${jobId}/sync-status`, { status: 'SCHEDULED' });
      } catch {}
      setJob((prev: any) => prev ? { ...prev, status: 'SCHEDULED' } : prev);
    }
    setSelectingWindow(false);
  }, [jobId, selectedWindowId, job, dispatch, selectingWindow]);

  // Advance status only when child screen (checklist/signature/loaded-photo) signals completion
  // via route.params.completedAction — NOT on simple goBack
  useEffect(() => {
    const completedAction = route.params?.completedAction as JobStatus | undefined;
    if (completedAction) {
      // Use the jobId passed back from the child screen to avoid updating the wrong job
      const targetJobId = route.params?.jobId || jobId;
      // Force GPS update on status transition
      try {
        if (Platform.OS === 'web' && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              api.post('/tracking/update', {
                jobId: targetJobId, latitude: pos.coords.latitude, longitude: pos.coords.longitude,
                heading: pos.coords.heading, speed: pos.coords.speed,
              }).catch(() => {});
            }, () => {}, { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 },
          );
        }
      } catch {}
      // Advance the SPECIFIC job that was completed (targetJobId) — NOT whatever job this
      // screen instance is currently showing. Previously this updated `prev` (the current
      // screen's job) and dispatched addActiveJob(prev), so completing a checklist for one
      // job could mark the WRONG job complete, and across multiple completions several jobs
      // ended up marked complete. advanceJobStatus updates only the job matching targetJobId.
      dispatch(advanceJobStatus({ jobId: targetJobId, status: completedAction }));
      setJob((prev: any) => (prev && prev.id === targetJobId ? { ...prev, status: completedAction } : prev));
      advancedViaAction.current = completedAction; // prevent sync-status from duplicating
      api.post(`/jobs/${targetJobId}/advance`, { status: completedAction }).catch(() => {
        // Fallback: force-sync if normal transition fails
        api.post(`/jobs/${targetJobId}/sync-status`, { status: completedAction }).catch(() => {});
      });
      // Clear the param so it doesn't re-trigger
      navigation.setParams({ completedAction: undefined });
    }
  }, [route.params?.completedAction, jobId, navigation, dispatch]);

  if (loading || !job) {
    return <View style={s.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;
  }

  // ── Cancelled State ── a cancelled job is no longer active; don't show the driver
  // the live workflow for it. (The board + active-job list already exclude it too.)
  if (job.status === 'CANCELLED') {
    return (
      <View style={s.completedWrap}>
        <View style={s.completedIcon}>
          <Ionicons name="close-circle" size={64} color={RD.textMuted} />
        </View>
        <Text style={s.completedTitle}>Job Cancelled</Text>
        <Text style={s.completedProduct}>This job was cancelled and is no longer active.</Text>
        <TouchableOpacity style={s.navigateBtn} onPress={() => navigation.goBack()} activeOpacity={0.85}>
          <Ionicons name="arrow-back" size={18} color="#fff" />
          <Text style={s.navigateBtnText}>Back to jobs</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ── Completed State ──
  // AWAITING_DELIVERY_PAYMENT is a legacy pay-on-delivery status; any old
  // in-flight order in that state is now treated as a completed delivery.
  if (job.status === 'COMPLETED' || job.status === 'BUYER_SIGNATURE_COMPLETE' || job.status === 'AWAITING_DELIVERY_PAYMENT') {
    return (
      <View style={s.completedWrap}>
        <View style={s.completedIcon}>
          <Ionicons name="checkmark-circle" size={64} color={RD.money} />
        </View>
        <Text style={s.completedTitle}>Delivery Complete!</Text>
        <Text style={s.completedProduct}>
          {job.displayTitle || (job.orderNumber ? `Order #${job.orderNumber}` : (job.productTitle || 'Pickup'))}
        </Text>
        {!isFleetMember && (
          <View style={s.completedPayoutCard}>
            <Text style={s.completedPayoutLabel}>Your Payout</Text>
            <Text style={s.completedPayoutAmount}>${Math.round(job.payoutAmount ?? 0)}</Text>
          </View>
        )}
        <View style={s.completedDetails}>
          <CompletedRow icon="arrow-up-circle" label="Picked up from" value={job.pickupAddress} />
          <CompletedRow icon="arrow-down-circle" label="Delivered to" value={job.deliveryAddress} />
          {(() => {
            if (!job.selectedWindow) return null;
            const paired = pairPickupWithDelivery(job.pickupWindows, job.deliveryWindows, job.scheduledWindowStart);
            const match = paired.find(p => p.deliveryWindowId === job.selectedWindow.id);
            if (!match) return null;
            const tzOpts: Intl.DateTimeFormatOptions = job.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
            const bp = bufferedPickup(match.pickupStart, match.pickupEnd);
            return (
              <CompletedRow icon="time" label="Pickup window" value={
                `${bp.start!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}${bp.end ? ` – ${bp.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}` : ''}`
              } />
            );
          })()}
        </View>
        {alreadyCompleted ? (
          <TouchableOpacity style={s.completedBtn} onPress={() => {
            navigation.reset({ index: 0, routes: [{ name: 'Main', params: { screen: 'History' } }] });
          }}>
            <Text style={s.completedBtnText}>Back to History</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={s.completedBtn} onPress={async () => {
            // Guard against a double-tap: completeJob dedups but addEarnings does NOT, so two
            // quick taps before the navigation.reset unmounts would double-count the payout.
            if (finalizingRef.current) return;
            finalizingRef.current = true;
            const payout = Math.round(job.payoutAmount ?? 0);

            // 🚨 CONFIRM WITH THE SERVER BEFORE TELLING A DRIVER THEY ARE DONE AND PAID.
            //
            // This marked the job complete and CREDITED THE PAYOUT in local state, fired
            // /complete as fire-and-forget with a swallowed sync-status fallback, and then
            // navigation.reset'd away. Both calls failing is not exotic - it is a weak signal
            // at a delivery address, which is most of them. The driver saw "complete" and
            // their earnings go up while the server never marked it COMPLETED, so no
            // WooCommerce update, no payment record and no notifications fired, dispatch
            // still showed the job open, and the reset left no way back to retry.
            //
            // Now: retry, then ask the server what the status actually is (a lost response
            // looks exactly like a failure from here), and only credit + navigate once it is
            // genuinely COMPLETED. On real failure the ref is released so they can tap again
            // rather than being stranded.
            let done = false;
            for (let attempt = 0; attempt < 3 && !done; attempt++) {
              try {
                await api.post(`/jobs/${jobId}/complete`);
                done = true;
              } catch {
                try {
                  await api.post(`/jobs/${jobId}/sync-status`, { status: 'COMPLETED' });
                  done = true;
                } catch {
                  if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
                }
              }
            }
            if (!done) {
              try {
                const { data } = await api.get(`/jobs/${jobId}`);
                if (data?.status === 'COMPLETED') done = true;
              } catch { /* still unreachable */ }
            }
            if (!done) {
              finalizingRef.current = false; // let them try again — never strand a driver
              Alert.alert(
                'Could not complete delivery',
                'We couldn’t reach the server to finish this job. Nothing is lost — please check your connection and tap Complete Delivery again.'
              );
              return;
            }

            dispatch(completeJob({ ...job, completedAt: new Date().toISOString() }));
            if (payout > 0) dispatch(addEarnings(payout));
            // Stop background location tracking
            stopBackgroundTracking().catch(() => {});
            // Navigate to Jobs tab
            navigation.reset({ index: 0, routes: [{ name: 'Main', params: { screen: 'History' } }] });
          }}>
            <Text style={s.completedBtnText}>Complete Delivery</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // ── Pickup-only terminal ──
  // For PICKUP_ONLY legs (long-haul handled by a third-party shipper), the
  // driver is done once the item is picked up and loaded. A shipper collects it
  // from them; our team coordinates the handoff directly.
  // A job is pickup-only by LEG (a split long-haul) or by TYPE. Every store-to-sell pickup is
  // jobType PICKUP_ONLY with legMode NULL, so checking legMode alone walked it into the buyer
  // half of the flow. A non-storage pickup-only job is done after the seller signs; a storage
  // one ends at the storage drop-off step instead (see workflowFor).
  const isStorageJob = job.categorySlug === 'storage';
  const isPickupOnly = job.legMode === 'PICKUP_ONLY' || (job as any).jobType === 'PICKUP_ONLY';
  if (isPickupOnly && (job.status === 'LOADED_PHOTO_COMPLETE'
    || (!isStorageJob && (job as any).jobType === 'PICKUP_ONLY' && job.status === 'SELLER_SIGNATURE_COMPLETE'))) {
    return (
      <View style={s.completedWrap}>
        <View style={s.completedIcon}><Ionicons name="checkmark-circle" size={64} color={RD.money} /></View>
        <Text style={s.completedTitle}>Pickup Complete!</Text>
        <Text style={s.completedProduct}>{job.displayTitle || (job.orderNumber ? `Order #${job.orderNumber}` : (job.productTitle || 'Pickup'))}</Text>
        {!isFleetMember && (job.payoutAmount ?? 0) > 0 && (
          <View style={s.completedPayoutCard}>
            <Text style={s.completedPayoutLabel}>Your Pickup Payout</Text>
            <Text style={s.completedPayoutAmount}>${Math.round(job.payoutAmount ?? 0)}</Text>
          </View>
        )}
        <View style={s.completedDetails}>
          <Text style={{ fontSize: 15, lineHeight: 22, color: RD.ink, textAlign: 'center' }}>
            This was a pickup only. A shipper will collect the item from you to take it the rest of the way, and our team will coordinate the handoff with you directly. You do not deliver this one. Thank you!
          </Text>
        </View>
        <TouchableOpacity style={s.completedBtn} onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Main', params: { screen: 'History' } }] })}>
          <Text style={s.completedBtnText}>Back to Jobs</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Delivery-only legs (long-haul handled by a shipper) skip the pickup half:
  // the driver receives the item and only does the last-mile delivery.
  const isDeliveryOnly = job.legMode === 'DELIVERY_ONLY';
  // The workflow now depends on WHAT THIS JOB IS. An assembly-only visit has no pickup
  // leg, so walking it through the standard table asked the driver to arrive at an
  // address that does not exist and then photograph an item that was never loaded.
  // Unknown/absent jobType falls back to DELIVERY, i.e. exactly the previous behaviour.
  const steps = workflowFor((job as any).jobType, { isStorage: isStorageJob });
  const hasPickupLeg = (job as any).hasPickup !== false;
  const hasDeliveryLeg = (job as any).hasDelivery !== false;
  let currentStep = steps.find(st => st.status === job.status);
  if (isDeliveryOnly && PICKUP_STATUSES.includes(job.status) && !(job.status === 'ACCEPTED' && !job.scheduledWindowStart)) {
    // Skip straight to "Head to Delivery" (the en-route-to-buyer action).
    currentStep = steps.find(st => st.status === 'LOADED_PHOTO_COMPLETE') || currentStep;
  }
  const currentIdx  = steps.findIndex(st => st.status === job.status);
  const totalSteps  = steps.length;
  const progress    = currentIdx >= 0 ? (currentIdx + 1) / totalSteps : 0;
  // Which contact/address the screen points at. A single-leg job has exactly one site, so
  // it must not flip to the other party's blank address halfway through.
  const isPickup    = !hasDeliveryLeg ? true
                    : !hasPickupLeg ? false
                    : isDeliveryOnly ? false
                    : isPickupPhase(job.status);

  const currentAddress = isPickup ? job.pickupAddress : job.deliveryAddress;
  // WHO IS ACTUALLY AT THE PICKUP. On a store-to-sell job the item is in our own storage
  // unit and the seller handed it over weeks ago, so naming her here sent the driver
  // looking for a person who was never going to be there. Every other job has no
  // pickupContactName and reads exactly as before.
  const pickupPerson   = job.pickupContactName || job.sellerName || 'Seller';
  const currentName    = isPickup ? pickupPerson : (job.buyerName || 'Buyer');
  const contactType: 'BUYER' | 'SELLER' = isPickup ? 'SELLER' : 'BUYER';

  // Drive time (Google) + on-site service/setup time so the driver sees the real
  // time commitment ("Xh drive + Ym service") and can plan the route themselves.
  const driveMin   = Number(job.driveTimeMinutes) || 0;
  const serviceMin = Number(job.serviceTimeMinutes) || 0;
  const fmtDur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`);

  // ── 4-phase stepper (Accepted · Pickup · En route · Delivered) ──
  // Derived from the detailed JOB_WORKFLOW status so we keep all real logic
  // while presenting the simplified v5 progress bar.
  const PHASE_LABELS = ['Accepted', 'Pickup', 'En route', 'Delivered'];
  const phaseIdx = (() => {
    switch (job.status) {
      case 'ACCEPTED':
      case 'SCHEDULED':
        return 0;
      case 'STARTED':
      case 'EN_ROUTE_TO_SELLER':
      case 'ARRIVED_PICKUP':
      case 'PICKUP_CHECKLIST_COMPLETE':
      case 'SELLER_SIGNATURE_COMPLETE':
        return 1;
      case 'LOADED_PHOTO_COMPLETE':
      case 'EN_ROUTE_TO_BUYER':
      case 'ARRIVED_DELIVERY':
      case 'DELIVERY_CHECKLIST_COMPLETE':
        return 2;
      default:
        return 3;
    }
  })();
  const heroInstruction = job.status === 'ACCEPTED' && !job.scheduledWindowStart
    ? 'Select Pickup Window'
    : STEP_DRIVER_INSTRUCTION[job.status] || currentStep?.label || job.status.replace(/_/g, ' ');

  return (
    <View style={s.container}>
      {/* Top Bar */}
      <View style={s.topBar}>
        <TouchableOpacity
          onPress={() => {
            if (showTips) { setShowTips(false); setShowDetailedTips(false); }
            else if (showFleetAssign) { setShowFleetAssign(false); }
            else if (navigation.canGoBack()) navigation.goBack();
            else navigation.navigate('Home');
          }}
          style={s.closeBtn}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          activeOpacity={0.7}
        >
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={s.topCenter}>
          <Text style={s.topTitle}>Active Delivery</Text>
          <Text style={s.topPhase}>{isPickup ? 'Pickup Phase' : 'Delivery Phase'}</Text>
        </View>
        <TouchableOpacity onPress={() => handleMessage(contactType)} style={s.msgIcon}>
          <Ionicons name="chatbubble-outline" size={22} color={RD.text} />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={s.scrollContent} showsVerticalScrollIndicator={false}>
        {/* 4-step progress stepper */}
        <View style={s.stepperRow}>
          {PHASE_LABELS.map((label, i) => (
            <View key={label} style={s.stepperItem}>
              <View style={[s.stepperBar, i <= phaseIdx && s.stepperBarActive]} />
              <Text style={[s.stepperLabel, i === phaseIdx && s.stepperLabelCurrent, i <= phaseIdx && s.stepperLabelDone]}>
                {label}
              </Text>
            </View>
          ))}
        </View>

        {/* Pickup-only banner */}
        {isPickupOnly && (
          <View style={{ backgroundColor: '#F1E7EE', borderRadius: 12, padding: 14, marginBottom: 10, flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
            <Ionicons name="cube-outline" size={20} color={RD.ink} />
            <Text style={{ flex: 1, fontSize: 13, color: RD.ink, lineHeight: 19 }}>
              <Text style={{ fontWeight: '700' }}>{isStorageJob ? 'Storage pickup. ' : 'Pickup only. '}</Text>
              {isStorageJob
                ? 'Collect the item from the seller, then take it to storage and confirm the drop-off with photos. There is no buyer on this job.'
                : "Pick up and load the item, then you're done. A shipper will collect it from you and our team will coordinate the handoff. You do not deliver this one."}
            </Text>
          </View>
        )}
        {/* Delivery-only banner */}
        {job.legMode === 'DELIVERY_ONLY' && (
          <View style={{ backgroundColor: '#E7EEF5', borderRadius: 12, padding: 14, marginBottom: 10, flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
            <Ionicons name="navigate-outline" size={20} color={'#16324f'} />
            <Text style={{ flex: 1, fontSize: 13, color: '#16324f', lineHeight: 19 }}>
              <Text style={{ fontWeight: '700' }}>Delivery only. </Text>
              The item is brought to you by a shipper — you handle only the last-mile delivery to the buyer. No pickup steps. Our team coordinates the handoff to you.
            </Text>
          </View>
        )}
        {/* Fleet view-only banner — live step + reassign control for the manager. */}
        {isFleetViewOnly && (
          <View style={{ backgroundColor: RD.amberSoft, borderRadius: 18, paddingVertical: 12, paddingHorizontal: 14, marginBottom: 8, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Ionicons name="eye-outline" size={18} color={RD.amber} />
              <Text style={{ flex: 1, fontSize: 13, color: RD.amber, fontWeight: '700' }}>
                {job.assignedFleetDriverName || 'Fleet driver'} is handling this — live view
              </Text>
              <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#1FA85A' }} />
            </View>
            <Text style={{ fontSize: 12.5, color: RD.amber, opacity: 0.9 }}>
              Now: {STEP_DRIVER_INSTRUCTION[job.status] || currentStep?.label || job.status.replace(/_/g, ' ')}{currentStep ? `  ·  step ${(JOB_WORKFLOW_STEPS.findIndex((st: any) => st.status === job.status) + 1) || 1} of ${JOB_WORKFLOW_STEPS.length}` : ''}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 2 }}>
              <TouchableOpacity onPress={() => setShowFleetAssign(true)} activeOpacity={0.8}
                style={{ flex: 1, backgroundColor: RD.maroon, borderRadius: 10, paddingVertical: 9, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 }}>
                <Ionicons name="swap-horizontal" size={15} color="#fff" />
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>Reassign driver</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => loadJob()} activeOpacity={0.8}
                style={{ backgroundColor: '#fff', borderRadius: 10, paddingVertical: 9, paddingHorizontal: 14, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6, borderWidth: 1, borderColor: RD.amber + '66' }}>
                <Ionicons name="refresh" size={15} color={RD.amber} />
                <Text style={{ color: RD.amber, fontWeight: '700', fontSize: 13 }}>Refresh</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        {/* Fleet manager: assign driver prompt */}
        {needsFleetAssignment && (
          <TouchableOpacity
            style={{ backgroundColor: RD.maroonSoft, borderRadius: 18, padding: 16, marginBottom: 8, flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderColor: RD.maroon + '40' }}
            onPress={() => setShowFleetAssign(true)}
            activeOpacity={0.7}
          >
            <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: RD.maroon, justifyContent: 'center', alignItems: 'center' }}>
              <Ionicons name="people" size={18} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 15, fontWeight: '700', color: RD.maroon }}>Assign a Driver</Text>
              <Text style={{ fontSize: 12, color: RD.maroon + 'BB', marginTop: 2 }}>Tap to assign this job to yourself or a fleet member</Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={RD.maroon} />
          </TouchableOpacity>
        )}
        {/* Dark "current step" hero card — what the driver does RIGHT NOW */}
        <View style={s.heroCard}>
          <View style={s.heroTopRow}>
            <View style={s.heroIconWrap}>
              <Ionicons name={(STEP_ICONS[job.status] || 'ellipse') as any} size={22} color={colors.white} />
            </View>
            <Text style={s.heroEyebrow}>Current step · {phaseIdx + 1} of 4</Text>
          </View>
          <Text style={s.heroInstruction}>{heroInstruction}</Text>
          <Text style={s.heroSub}>
            {isPickup
              ? `Meet ${job.pickupContactName || job.sellerName || 'the seller'}, inspect the item and complete the checklist before loading.`
              : `Deliver to ${job.buyerName || 'the buyer'}, complete the checklist and capture the signature.`}
          </Text>
        </View>

        {/* Certificate of insurance: a building on this job needs one, and the driver can upload
            their own (owner, 28 Oct 2026). Renders nothing when job.coi.needed is false. */}
        <CoiCard jobId={job.id || jobId} summary={(job as any).coi} canUpload />

        {/* Pickup Windows — seller-provided times, admin-style Select buttons. NEVER show delivery windows. */}
        {(() => {
          const effectiveDelivery = deliveryWindows.length > 0 ? deliveryWindows : (job.deliveryWindows || []);
          const paired = pairPickupWithDelivery(job.pickupWindows, effectiveDelivery, job.scheduledWindowStart);
          if (paired.length === 0) {
            // Pickup availability hasn't been provided by the seller yet — can't schedule.
            if (job.status === 'ACCEPTED' && !job.scheduledWindowStart) {
              return (
                <View style={[s.sectionCard, { gap: 6 }]}>
                  <Text style={{ fontSize: 16, fontWeight: '700', color: RD.text }}>Pickup Window</Text>
                  <View style={{ height: 1, backgroundColor: RD.border, marginBottom: 2 }} />
                  <View style={{ borderRadius: 12, padding: 14, backgroundColor: RD.amberSoft, borderWidth: 1, borderColor: RD.amber + '55' }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: RD.text, marginBottom: 4 }}>Waiting on seller availability</Text>
                    <Text style={{ fontSize: 12, color: RD.textSec, lineHeight: 17 }}>
                      The seller hasn't shared pickup times yet. You'll be able to schedule this job once they do — we'll text you.
                    </Text>
                  </View>
                </View>
              );
            }
            return null;
          }
          const tzOpts: Intl.DateTimeFormatOptions = job.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
          return (
            <View style={[s.sectionCard, { gap: 10 }]}>
              <Text style={{ fontSize: 16, fontWeight: '700', color: RD.text }}>{paired.length === 1 ? 'Pickup Window' : 'Pickup Windows'}</Text>
              <View style={{ height: 1, backgroundColor: RD.border, marginBottom: 2 }} />
              {paired.map((p, i) => {
                const bp = bufferedPickup(p.pickupStart, p.pickupEnd);
                const startStr = bp.start!.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...tzOpts }) + ', ' +
                  bp.start!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts });
                const endStr = (bp.end ?? bp.start!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...tzOpts }) + ', ' +
                  (bp.end ?? bp.start!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts });
                return (
                  <View
                    key={p.deliveryWindowId}
                    style={{
                      flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                      padding: 14, borderRadius: 12, minHeight: 48,
                      borderWidth: p.isSelected ? 1.5 : 1,
                      borderColor: p.isSelected ? RD.money : RD.border,
                      backgroundColor: p.isSelected ? RD.moneySoft : RD.g03,
                    }}
                  >
                    <Text style={{ flex: 1, fontSize: 14, color: RD.text }}>
                      {startStr} — {endStr}
                    </Text>
                    {p.isSelected ? (
                      <View style={{ backgroundColor: RD.money, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 6 }}>
                        <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700' }}>Scheduled</Text>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={{ backgroundColor: RD.ink, borderRadius: 12, paddingHorizontal: 18, paddingVertical: 10, minHeight: 40, justifyContent: 'center' }}
                        onPress={async () => {
                          setSelectingWindow(true);
                          setSelectedWindowId(p.deliveryWindowId);
                          advancedViaAction.current = 'SCHEDULED';
                          try {
                            await api.post(`/jobs/${jobId}/select-window`, { windowId: p.deliveryWindowId });
                            // Functional update from the latest job (avoid reverting a status
                            // the socket/poll advanced between render and this tap).
                            setJob((prev: any) => {
                              const next = prev ? { ...prev, status: 'SCHEDULED', scheduledWindowStart: p.deliveryStartTime, scheduledWindowEnd: p.deliveryEndTime } : prev;
                              if (next) dispatch(addActiveJob(next));
                              return next;
                            });
                            await loadJob();
                          } catch (err: any) {
                            try {
                              await api.post(`/jobs/${jobId}/sync-status`, { status: 'SCHEDULED' });
                            } catch {}
                            setJob((prev: any) => prev ? { ...prev, status: 'SCHEDULED', scheduledWindowStart: p.deliveryStartTime, scheduledWindowEnd: p.deliveryEndTime } : prev);
                          }
                          setSelectingWindow(false);
                        }}
                        disabled={selectingWindow}
                        activeOpacity={0.8}
                      >
                        {selectingWindow && selectedWindowId === p.deliveryWindowId ? (
                          <ActivityIndicator color="#fff" size="small" />
                        ) : (
                          <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700' }}>Select</Text>
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                );
              })}
            </View>
          );
        })()}

        {/* Item card */}
        <View style={s.itemCard}>
          <View style={s.itemThumb} />
          <View style={s.itemInfo}>
            <Text style={s.itemTitle} numberOfLines={2}>{job.productTitle}</Text>
            <Text style={s.itemSub} numberOfLines={1}>
              {getTrainingCategoryLabel(job.categorySlug || 'general')}
              {(() => {
                // Crew = the real helpersNeeded (TOTAL people incl. driver) so it matches the job
                // board + detail screens. Only fall back to a hot-tub guess when it's unset.
                const isHotTub = (job.categorySlug || '').includes('hot-tub') || (job.productTitle || '').toLowerCase().includes('hot tub');
                const total = job.helpersNeeded && job.helpersNeeded > 0 ? job.helpersNeeded : (isHotTub ? 3 : 1);
                const helpers = total - 1;
                return helpers > 0 ? ` · 1 driver + ${helpers} helper${helpers > 1 ? 's' : ''}` : '';
              })()}
            </Text>
          </View>
          {!isFleetMember && (
            <Text style={s.itemPayout}>${Math.round(job.payoutAmount ?? 0)}</Text>
          )}
        </View>

        {/* Full pickup AND delivery addresses — always shown so the driver can see the
            whole route and map it themselves. Tap either to navigate there. */}
        {job.pickupAddress ? (
          <TouchableOpacity onPress={() => openNavigation(job.pickupAddress)} activeOpacity={0.85} style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start', backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderRadius: 14, padding: 14, marginBottom: 8 }}>
            <Ionicons name="location" size={18} color={RD.maroon} style={{ marginTop: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: RD.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Pickup address</Text>
              <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text, lineHeight: 21 }}>{job.pickupAddress}</Text>
            </View>
            <Ionicons name="navigate-outline" size={16} color={RD.textMuted} style={{ marginTop: 2 }} />
          </TouchableOpacity>
        ) : null}
        {job.deliveryAddress ? (
          <TouchableOpacity onPress={() => openNavigation(job.deliveryAddress)} activeOpacity={0.85} style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start', backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderRadius: 14, padding: 14, marginBottom: 12 }}>
            <Ionicons name="location" size={18} color={RD.ink} style={{ marginTop: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: RD.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Delivery address</Text>
              <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text, lineHeight: 21 }}>{job.deliveryAddress}</Text>
            </View>
            <Ionicons name="navigate-outline" size={16} color={RD.textMuted} style={{ marginTop: 2 }} />
          </TouchableOpacity>
        ) : null}

        {/* Navigate to current destination */}
        <TouchableOpacity style={s.navigateBtn} onPress={() => openNavigation(currentAddress)} activeOpacity={0.85}>
          <Ionicons name="navigate" size={18} color="#fff" />
          <Text style={s.navigateBtnText}>Navigate to {isPickup ? 'pickup' : 'delivery'}</Text>
        </TouchableOpacity>

        {/* Contacts card — Call + Message both parties */}
        <View style={s.contactsCard}>
          <Text style={s.contactsTitle}>Contacts</Text>
          {[
            { role: 'Pickup', name: pickupPerson, kind: job.pickupContactName ? 'Commonplace' : 'Seller', icon: 'storefront', soft: RD.maroonSoft, col: RD.maroon, recipient: 'SELLER' as const },
            { role: 'Delivery', name: job.buyerName || 'Buyer', kind: 'Buyer', icon: 'person', soft: RD.g05, col: RD.ink, recipient: 'BUYER' as const },
          ].map((c, i) => (
            <View key={c.role} style={[s.contactRow, i > 0 && s.contactRowDivider]}>
              <View style={[s.contactAvatar, { backgroundColor: c.soft }]}>
                <Ionicons name={c.icon as any} size={19} color={c.col} />
              </View>
              <View style={s.contactMeta}>
                <Text style={s.contactName}>
                  {c.name} <Text style={s.contactKind}>· {c.kind}</Text>
                </Text>
                <Text style={s.contactRoleLabel}>{c.role} contact</Text>
              </View>
              {/* v5 contacts: a single labeled Message button (no calling) */}
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 16, height: 40, borderRadius: 20, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface }}
                onPress={() => handleMessage(c.recipient)}
                activeOpacity={0.7}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="chatbubble-outline" size={16} color={RD.text} />
                <Text style={{ fontSize: 14, fontWeight: '700', color: RD.text }}>Message</Text>
              </TouchableOpacity>
            </View>
          ))}
          <View style={s.privacyNote}>
            <Ionicons name="shield-checkmark-outline" size={13} color={RD.textFaint} />
            <Text style={s.privacyText}>Messages go through Commonplace — numbers stay private.</Text>
          </View>
        </View>

        {/* Pickup Window Summary — read-only. Shows the driver-facing BUFFERED arrival
            window (30 min earlier start, 1 hr earlier end) so they arrive on time. */}
        {!isPickup && driverWindowStart(job) && (
          <View style={s.windowCard}>
            <Ionicons name="time-outline" size={16} color={RD.textSec} />
            <View>
              <Text style={s.windowDay}>
                Pickup · {driverWindowStart(job)!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(job.pickupTimezone ? { timeZone: job.pickupTimezone } : {}) })}
              </Text>
              <Text style={s.windowTime}>
                {driverWindowStart(job)!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(job.pickupTimezone ? { timeZone: job.pickupTimezone } : {}) })}{driverWindowEnd(job) ? ` – ${driverWindowEnd(job)!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(job.pickupTimezone ? { timeZone: job.pickupTimezone } : {}) })}` : ''}
              </Text>
            </View>
          </View>
        )}

        {/* TWO-DAY jobs only: the driver also sees the DELIVERY window, because the delivery
            is a separate day's work. On every other job the delivery window is derived from
            the pickup and shown to nobody but the buyer. */}
        {fmtTwoDayDelivery(job) && (
          <View style={s.windowCard}>
            <Ionicons name="moon-outline" size={16} color={RD.textSec} />
            <View>
              <Text style={s.windowDay}>Delivery · next day</Text>
              <Text style={s.windowTime}>{fmtTwoDayDelivery(job)}</Text>
            </View>
          </View>
        )}

        {/* Route Map — Live route from pickup to delivery */}
        {job.pickupAddress && job.deliveryAddress && (
          <View style={s.mapCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Text style={s.mapCardTitle}>Route</Text>
              {driveMin > 0 ? <Text style={{ fontSize: 13, fontWeight: '800', color: RD.maroon }}>{fmtDur(driveMin)} drive</Text> : null}
            </View>
            {(driveMin > 0 || serviceMin > 0) ? (
              <Text style={{ fontSize: 12.5, color: RD.textMuted, marginTop: 2, marginBottom: 10 }}>
                Est. {fmtDur(driveMin)} drive{serviceMin > 0 ? ` + ${fmtDur(serviceMin)} on-site service` : ''}{(driveMin + serviceMin) > 0 ? ` = ${fmtDur(driveMin + serviceMin)} total` : ''}
              </Text>
            ) : null}
            <RouteMap
              pickupAddress={job.pickupAddress}
              deliveryAddress={job.deliveryAddress}
              driverLocation={driverLocation}
              activePhase={isPickup ? 'pickup' : 'delivery'}
              height={240}
            />
            <View style={s.mapLegend}>
              <View style={s.legendItem}>
                <View style={[s.legendDot, { backgroundColor: RD.amber }]} />
                <Text style={s.legendText}>Pickup</Text>
              </View>
              <View style={s.legendItem}>
                <View style={[s.legendDot, { backgroundColor: RD.ink }]} />
                <Text style={s.legendText}>Delivery</Text>
              </View>
              {driverLocation && (
                <View style={s.legendItem}>
                  <View style={[s.legendDot, { backgroundColor: RD.info }]} />
                  <Text style={s.legendText}>You</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Support Contact */}
        <TouchableOpacity
          style={s.supportCard}
          onPress={() => navigation.navigate('SupportInbox')}
        >
          <Ionicons name="headset-outline" size={18} color={RD.textSec} />
          <View style={s.supportInfo}>
            <Text style={s.supportTitle}>Need Help?</Text>
            <Text style={s.supportSub}>Contact Commonplace support</Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color={RD.g30} />
        </TouchableOpacity>
      </ScrollView>

      {/* Bottom Action — hidden when fleet manager viewing someone else's job */}
      {!isFleetViewOnly && <View style={s.bottomPanel}>
        <ActionButton
          step={currentStep}
          job={job}
          onAction={handleAction}
          onChecklist={handleChecklistNav}
          onSignature={handleSignatureNav}
          onLoadedPhoto={handleLoadedPhotoNav}
          onStorageDropOff={handleStorageDropOffNav}
          advancing={advancing}
        />
      </View>}

      {/* Fleet Assignment Modal */}
      <Modal visible={showFleetAssign} transparent animationType="slide" onRequestClose={() => setShowFleetAssign(false)}>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <TouchableOpacity style={{ ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' }} onPress={() => setShowFleetAssign(false)} activeOpacity={1} />
          <View style={{ backgroundColor: RD.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 40, maxHeight: '85%' }}>
            <View style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, alignSelf: 'center', marginBottom: 16 }} />
            <Text style={{ fontSize: 20, fontWeight: '700', color: RD.text, marginBottom: 4 }}>Assign Driver</Text>
            <Text style={{ fontSize: 13, color: RD.textMuted, marginBottom: 16 }}>
              {job?.productTitle || 'Job'}
            </Text>
            {/* 🚨 CAPPING THE LIST DID NOT CAP THE SHEET, AND NORMAN STILL COULD NOT REACH
                THE TOP OF IT.
                The 8 Sep fix put the members in a ScrollView capped at half of
                `Dimensions.get('window').height`. The LIST then behaved - but the sheet
                around it did not: it is anchored to the BOTTOM of a flex:1 parent, so
                header + list + spinner + Cancel + 64px of padding still added up past the
                screen and the overflow went off the TOP. Norman's screenshot on 11 Sep has
                no grab handle, no "Assign Driver" title and no "Myself" row in it at all -
                they are above the notch. He is not failing to scroll a list; he is looking
                at a sheet whose top does not exist.
                Also: a Dimensions read is a number taken once, against a viewport that is
                not the same as this sheet's parent on an installed PWA. Bounding the SHEET
                at 85% and letting the list flexShrink into what is left is resolved by the
                layout engine against the real parent, on both native and web, and needs no
                number at all. */}
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ paddingBottom: 2 }} showsVerticalScrollIndicator>
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, backgroundColor: RD.g03, borderWidth: 1, borderColor: RD.border, marginBottom: 8, minHeight: 48 }}
                onPress={() => handleFleetAssign('self')}
                disabled={assigningFleet}
              >
                <Ionicons name="person-circle" size={28} color={RD.textSec} />
                <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text, flex: 1 }}>Myself</Text>
                <Ionicons name="chevron-forward" size={16} color={RD.g30} />
              </TouchableOpacity>
              {fleetMembers.map((m: any) => (
                <TouchableOpacity
                  key={m.id}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, backgroundColor: RD.g03, borderWidth: 1, borderColor: RD.border, marginBottom: 8, minHeight: 48 }}
                  onPress={() => handleFleetAssign(m.id)}
                  disabled={assigningFleet}
                >
                  <Ionicons name="person" size={24} color={RD.textSec} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: RD.text }}>{m.name}</Text>
                    {m.phone && <Text style={{ fontSize: 12, color: RD.textFaint }}>{m.phone}</Text>}
                  </View>
                  <Ionicons name="chevron-forward" size={16} color={RD.g30} />
                </TouchableOpacity>
              ))}
            </ScrollView>
            {assigningFleet && <ActivityIndicator size="small" color={RD.textSec} style={{ marginTop: 8 }} />}
            <TouchableOpacity onPress={() => setShowFleetAssign(false)} style={{ alignSelf: 'center', padding: 12, marginTop: 8, minHeight: 48, justifyContent: 'center' }}>
              <Text style={{ color: RD.textMuted, fontWeight: '600' }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Driver Tips & Item Info FAB */}
      {!!job && (
        <Animated.View style={[s.tipsFab, !tipsViewed && { backgroundColor: RD.info, opacity: tipsPulse }]}>
          <TouchableOpacity
            style={{ width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center' }}
            onPress={() => openTips()}
            activeOpacity={0.8}
          >
            <Ionicons name="bulb-outline" size={22} color={colors.white} />
          </TouchableOpacity>
        </Animated.View>
      )}

      {/* Tips & Item Info Modal */}
      {!!job && (
        <Modal visible={showTips} transparent animationType="slide" onRequestClose={() => { setShowTips(false); setShowDetailedTips(false); }}>
          <View style={s.tipsOverlay}>
            <View style={s.tipsSheet}>
              <View style={s.tipsHeader}>
                <Text style={s.tipsTitle}>Helpful Hints</Text>
                <TouchableOpacity onPress={() => { setShowTips(false); setShowDetailedTips(false); }} style={{ padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' }} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
                  <Ionicons name="close" size={24} color={RD.textSec} />
                </TouchableOpacity>
              </View>
              <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }}>
                {/* Item first, category second: components/LightbulbBody.tsx. */}
                <LightbulbBody job={job} note={tipsGateNote} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

    </View>
  );
}

function CompletedRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <View style={s.completedRow}>
      <Ionicons name={icon as any} size={16} color={RD.textMuted} />
      <View style={s.completedRowInfo}>
        <Text style={s.completedRowLabel}>{label}</Text>
        <Text style={s.completedRowValue}>{value}</Text>
      </View>
    </View>
  );
}

function ActionButton({ step, job, onAction, onChecklist, onSignature, onLoadedPhoto, onStorageDropOff, advancing }: any) {
  if (!step) return null;

  const { action, endpoint, label, requiresChecklist, requiresSignature, requiresLoadedPhoto, signaturePhase } = step;
  const isPickup = isPickupPhase(job.status);

  let icon = 'arrow-forward';
  if (requiresChecklist) icon = 'clipboard-outline';
  if (requiresSignature) icon = 'create-outline';
  if (requiresLoadedPhoto) icon = 'camera-outline';
  if (endpoint.includes('en-route')) icon = 'navigate-outline';
  if (endpoint.includes('arrive')) icon = 'location-outline';
  if (endpoint.includes('start')) icon = 'play-outline';
  if (endpoint === 'storage-drop-off') icon = 'cube-outline';

  const onPress = endpoint === 'storage-drop-off'
    ? () => onStorageDropOff?.()
    : requiresChecklist
    ? () => onChecklist(isPickup ? 'pickup' : 'delivery')
    : requiresSignature
      ? () => onSignature(signaturePhase)
      : requiresLoadedPhoto
        ? () => onLoadedPhoto()
        : () => onAction(endpoint, action);

  return (
    <TouchableOpacity style={s.primaryBtn} onPress={onPress} disabled={advancing} activeOpacity={0.8}>
      {advancing ? (
        <ActivityIndicator color={colors.white} />
      ) : (
        <>
          <Ionicons name={icon as any} size={20} color={colors.white} />
          <Text style={s.primaryBtnText}>{label}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const s = StyleSheet.create({
  container:         { flex: 1, backgroundColor: RD.bg },
  loadingWrap:       { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg },

  // Reusable warm-premium card
  sectionCard:       { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },

  // ── Completed ──
  completedWrap:     { flex: 1, backgroundColor: RD.bg, padding: spacing.xl },
  completedIcon:     { alignSelf: 'center', marginTop: 80, marginBottom: spacing.lg },
  completedTitle:    { fontSize: 28, fontWeight: '700', color: RD.text, textAlign: 'center', marginBottom: 6 },
  completedProduct:  { fontSize: 15, color: RD.textMuted, textAlign: 'center', marginBottom: spacing.lg },
  completedPayoutCard: { backgroundColor: RD.ink, borderRadius: 18, padding: spacing.lg, alignItems: 'center', marginBottom: spacing.lg, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  completedPayoutLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: '600', marginBottom: 4 },
  completedPayoutAmount: { fontSize: 42, fontWeight: '800', color: '#fff' },
  completedDetails:  { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: spacing.base, gap: 12, marginBottom: spacing.xxl, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  completedRow:      { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  completedRowInfo:  { flex: 1 },
  completedRowLabel: { fontSize: 11, fontWeight: '700', color: RD.textFaint, letterSpacing: 0.3, marginBottom: 2 },
  completedRowValue: { fontSize: 14, color: RD.textSec, lineHeight: 20 },
  completedBtn:      { backgroundColor: RD.ink, borderRadius: 16, height: 54, justifyContent: 'center', alignItems: 'center' },
  completedBtnText:  { color: '#fff', fontWeight: '600', fontSize: 16 },

  // ── Top Bar ──
  topBar:            { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  closeBtn:          { padding: 10, minWidth: 48, minHeight: 48, justifyContent: 'center' },
  topCenter:         { alignItems: 'center' },
  topTitle:          { fontSize: 19, fontWeight: '700', color: RD.text },
  topPhase:          { fontSize: 12, fontWeight: '600', color: RD.textFaint, marginTop: 1 },
  msgIcon:           { padding: 12, minWidth: 48, minHeight: 48, justifyContent: 'center', alignItems: 'center' },

  // ── 4-step Progress Stepper ──
  stepperRow:        { flexDirection: 'row', gap: 7 },
  stepperItem:       { flex: 1 },
  stepperBar:        { height: 5, borderRadius: 3, backgroundColor: RD.g10 },
  stepperBarActive:  { backgroundColor: RD.ink },
  stepperLabel:      { fontSize: 10.5, fontWeight: '500', color: RD.g40, marginTop: 6, textAlign: 'center' },
  stepperLabelDone:  { color: RD.text },
  stepperLabelCurrent: { fontWeight: '700' },

  scrollContent:     { padding: spacing.lg, gap: 14, paddingBottom: 160 },

  // ── Dark "current step" Hero Card ──
  heroCard:          { backgroundColor: RD.ink, borderRadius: 18, padding: spacing.lg, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  heroTopRow:        { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  heroIconWrap:      { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  heroEyebrow:       { fontSize: 11, fontWeight: '700', color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5, textTransform: 'uppercase' },
  heroInstruction:   { fontSize: 20, fontWeight: '700', color: '#fff', letterSpacing: -0.3 },
  heroSub:           { fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 5, lineHeight: 19 },

  // ── Item Card ──
  itemCard:          { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16, flexDirection: 'row', alignItems: 'center', gap: 12, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  itemThumb:         { width: 46, height: 46, borderRadius: 12, backgroundColor: RD.g07, borderWidth: 1, borderColor: RD.g10 },
  itemInfo:          { flex: 1 },
  itemTitle:         { fontSize: 14.5, fontWeight: '700', color: RD.text },
  itemSub:           { fontSize: 12.5, color: RD.textMuted, marginTop: 1 },
  itemPayout:        { fontSize: 16, fontWeight: '700', color: RD.money, marginLeft: 6 },

  // ── Navigate Button ──
  navigateBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 50, borderRadius: 16, backgroundColor: RD.ink },
  navigateBtnText:   { fontSize: 15, fontWeight: '700', color: '#fff' },

  // ── Contacts Card ──
  contactsCard:      { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, paddingHorizontal: 16, paddingVertical: 6, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  contactsTitle:     { fontSize: 14, fontWeight: '700', color: RD.text, paddingTop: 10, paddingBottom: 4 },
  contactRow:        { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12 },
  contactRowDivider: { borderTopWidth: 1, borderTopColor: RD.g07 },
  contactAvatar:     { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  contactMeta:       { flex: 1, minWidth: 0 },
  contactName:       { fontSize: 14, fontWeight: '700', color: RD.text },
  contactKind:       { fontSize: 14, fontWeight: '500', color: RD.g40 },
  contactRoleLabel:  { fontSize: 11.5, color: RD.g40, marginTop: 1 },
  contactActions:    { flexDirection: 'row', gap: 8 },
  contactIconBtn:    { width: 40, height: 40, borderRadius: 12, backgroundColor: RD.g05, borderWidth: 1, borderColor: RD.border, justifyContent: 'center', alignItems: 'center' },
  privacyNote:       { flexDirection: 'row', alignItems: 'center', gap: 6, paddingTop: 8, paddingBottom: 10 },
  privacyText:       { fontSize: 11, color: RD.textFaint, flex: 1 },

  // ── Window Card ──
  windowCard:        { backgroundColor: RD.g03, borderRadius: 14, borderWidth: 1, borderColor: RD.border, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 },
  windowDay:         { fontSize: 14, fontWeight: '600', color: RD.text },
  windowTime:        { fontSize: 12, color: RD.textMuted, marginTop: 1 },

  // ── Map Card ──
  mapCard:           { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  mapCardTitle:      { fontSize: 14, fontWeight: '700', color: RD.text, marginBottom: spacing.sm },
  mapLegend:         { flexDirection: 'row', gap: 16, marginTop: spacing.sm, justifyContent: 'center' },
  legendItem:        { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot:         { width: 8, height: 8, borderRadius: 4 },
  legendText:        { fontSize: 11, color: RD.textMuted, fontWeight: '600' },

  // ── Support ──
  supportCard:       { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16, flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 48, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  supportInfo:       { flex: 1 },
  supportTitle:      { fontSize: 14, fontWeight: '600', color: RD.text },
  supportSub:        { fontSize: 12, color: RD.textMuted, marginTop: 1 },

  // ── Bottom ──
  bottomPanel:       { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: RD.surface, padding: spacing.lg, paddingBottom: Platform.OS === 'ios' ? 36 : 20, borderTopWidth: 1, borderTopColor: RD.border },
  primaryBtn:        { backgroundColor: RD.ink, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 10 },
  primaryBtnText:    { color: '#fff', fontWeight: '600', fontSize: 17, letterSpacing: 0.2 },

  // ── Window Picker ──
  windowPickerCard:  { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16 },
  windowPickerTitle: { fontSize: 16, fontWeight: '700', color: RD.text, marginBottom: 4 },
  windowPickerSubtitle: { fontSize: 13, color: RD.textMuted, marginBottom: 14 },
  windowOption:      { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1.5, borderColor: RD.border, marginBottom: 8, minHeight: 48 },
  windowOptionSelected: { borderColor: RD.money, backgroundColor: RD.moneySoft },
  windowRadio:       { width: 22, height: 22, borderRadius: 11, borderWidth: 2, borderColor: RD.g30, justifyContent: 'center', alignItems: 'center' },
  windowRadioSelected: { borderColor: RD.money },
  windowRadioInner:  { width: 12, height: 12, borderRadius: 6, backgroundColor: RD.money },
  windowOptionDay:   { fontSize: 15, fontWeight: '600', color: RD.text },
  windowOptionTime:  { fontSize: 13, color: RD.textSec, marginTop: 2 },
  selectWindowBtn:   { backgroundColor: RD.ink, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 10 },
  selectWindowBtnText: { color: '#fff', fontWeight: '600', fontSize: 17, letterSpacing: 0.2 },

  // ── Driver Tips ──
  tipsFab:           { position: 'absolute', bottom: 100, right: 20, width: 48, height: 48, borderRadius: 24, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, elevation: 5, zIndex: 10 },
  tipsOverlay:       { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  tipsSheet:         { backgroundColor: RD.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '85%', padding: 20 },
  tipsHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  tipsTitle:         { fontSize: 20, fontWeight: '700', color: RD.text },
  tipRow:            { flexDirection: 'row', gap: 10, marginBottom: 12, alignItems: 'flex-start' },
  tipBullet:         { width: 22, height: 22, borderRadius: 11, backgroundColor: RD.ink, color: '#fff', fontSize: 12, fontWeight: '700', textAlign: 'center', lineHeight: 22, overflow: 'hidden' },
  tipText:           { flex: 1, fontSize: 14, color: RD.textSec, lineHeight: 20 },
  videoRow:          { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: RD.g03, borderRadius: 12, borderWidth: 1, borderColor: RD.border, marginBottom: 8, minHeight: 48 },
  videoTitle:        { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text },
});
